/**
 * Spaltengeometrie der Frametabelle.
 *
 * Reihenfolge und Indizes sind durch CONTRACTS.md §6 eingefroren
 * (TABLE_COLUMN_TITLES) — hier kommen nur Breiten, Ausrichtung und
 * Textfarbstufe dazu, abgeleitet aus CreateControls()/OnCustomDraw()/
 * OnHeaderCustomDraw() in CanMonitorApp.cpp.
 */

import { TABLE_COLUMN_COUNT, TABLE_COLUMN_TITLES } from '../../app/settings';

export { TABLE_COLUMN_COUNT, TABLE_COLUMN_TITLES };

/** Spaltenindizes, auf die sich Sonderbehandlungen beziehen. */
export const COL = {
  seq: 0,
  id: 1,
  dlc: 2,
  firstByte: 3,
  lastByte: 10,
  name: 11,
  description: 12,
  dt: 13,
} as const;

/**
 * Textfarbstufe je Spalte (OnCustomDraw, CDDS_SUBITEM): die ID ist der
 * Akzent-"Held", die Rohbytes sind hoch gewichtet, dt(ms) ist Chrome,
 * alles andere bleibt auf der gedämpften Standardstufe.
 */
export type ColumnTier = 'accent' | 'primary' | 'dim' | 'muted';

export function columnTier(col: number): ColumnTier {
  if (col === COL.id) return 'accent';
  if (col >= COL.firstByte && col <= COL.lastByte) return 'primary';
  if (col === COL.dt) return 'dim';
  return 'muted';
}

/** Zellinhalt zentriert: ID, DLC, Name (OnCustomDraw). */
export function isCellCentered(col: number): boolean {
  return col === COL.id || col === COL.dlc || col === COL.name;
}

/** Überschrift zentriert: n, ID Hex, Name, Description (OnHeaderCustomDraw). */
export function isHeaderCentered(col: number): boolean {
  return col === COL.seq || col === COL.id || col === COL.name || col === COL.description;
}

/** Nur die ID-Spalte wird fett gezeichnet (GetConsolasFont(11, true)). */
export function isCellBold(col: number): boolean {
  return col === COL.id;
}

export interface WidthMetrics {
  /** ScalePx() — rohe Pixelliterale mal UI-Skalierung. */
  scalePx: (basePixels: number) => number;
  /** FlatButtonWidth() — Textbreite plus ScalePx(24) Polsterung. */
  flatButtonWidth: (text: string) => number;
}

/**
 * Vorgabebreiten wie in CreateControls(). `descriptionWidth` entspricht
 * ComputeDescriptionColumnWidth(): das Original schätzt daraus die längste
 * Zusammenfassung, die die geladene DBC/EDS überhaupt erzeugen kann. Diese
 * Kataloge leben in der PWA im Worker, deshalb reicht der Aufrufer den Wert
 * herein; ohne Angabe wird nur die Überschrift eingepasst.
 */
export function defaultColumnWidths(metrics: WidthMetrics, descriptionWidth?: number): number[] {
  const byteWidth = metrics.scalePx(38);
  return [
    metrics.flatButtonWidth('10000'),
    metrics.flatButtonWidth('1C FEFF 80'),
    metrics.scalePx(45),
    byteWidth,
    byteWidth,
    byteWidth,
    byteWidth,
    byteWidth,
    byteWidth,
    byteWidth,
    byteWidth,
    metrics.flatButtonWidth('Name'),
    descriptionWidth ?? metrics.flatButtonWidth('Description'),
    metrics.scalePx(90),
  ];
}

/**
 * Schätzt die Breite der Description-Spalte aus den Texten, die die geladenen
 * Kataloge erzeugen können — Portierung von ComputeDescriptionColumnWidth().
 * `sampleTexts` liefert AP-6 aus den DBC-/EDS-Snapshots; die Schrift ist
 * dicktengleich, daher genügt die Zeichenanzahl mal Zeichenbreite. Die
 * Deckelung bei 400 px hält dt(ms) auch bei langen Beschreibungen sichtbar.
 */
export function describeColumnWidth(
  sampleTexts: Iterable<string>,
  charWidth: number,
  metrics: WidthMetrics,
): number {
  let longestChars = 'Description'.length;
  for (const text of sampleTexts) {
    if (text.length > longestChars) longestChars = text.length;
  }
  const estimated = Math.ceil(longestChars * charWidth) + metrics.scalePx(24);
  return Math.max(metrics.flatButtonWidth('Description'), Math.min(estimated, metrics.scalePx(400)));
}
